import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import StatsAPI from "@/src/api/StatsAPI";

import ExportCsvButton from "../../charts/ExportCsvButton";
import TimelineQueryCard from "../../charts/TimelineQueryCard";
import { trialUnsubscriptionsViewsQuery } from "../offerAnalytics.queries";
import { buildTrialTimeline } from "../offerAnalytics.utils";

import type { JSX } from "react";

import type { GetOffersTrialUnsubscriptionsViewsResponse } from "@/src/api/StatsAPI.types";

import type { ChartSeries, TimelineDatum } from "../../charts/charts.types";
import type {
  CountSeriesKey,
  OfferAnalyticsFilterProps,
} from "../offerAnalytics.types";

function buildTimeline(
  response: GetOffersTrialUnsubscriptionsViewsResponse | undefined,
): TimelineDatum<CountSeriesKey>[] {
  return buildTrialTimeline(response?.data ?? []);
}

function TrialUnsubscriptionsCard({
  filters,
}: OfferAnalyticsFilterProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");

  const seriesList = useMemo(
    (): ChartSeries<CountSeriesKey>[] => [
      {
        key: "count",
        label: t("offerAnalytics.series.unsubscriptions"),
        color: "var(--chart-1)",
      },
    ],
    [t],
  );
  const fetchCsv = async (): Promise<BlobPart> =>
    // With isExport the endpoint answers raw CSV instead of the typed JSON.
    (await StatsAPI.getOffersTrialUnsubscriptionsViews({
      startDate: filters.startDate,
      endDate: filters.endDate,
      isExport: true,
      projectSlug,
    })) as unknown as BlobPart;

  return (
    <TimelineQueryCard
      buildTimeline={buildTimeline}
      endDate={filters.endDate}
      isNotFilterableByOffer={!!filters.offerUuid}
      query={trialUnsubscriptionsViewsQuery(projectSlug, filters)}
      seriesList={seriesList}
      startDate={filters.startDate}
      title={t("offerAnalytics.charts.trialUnsubscriptions")}
      extraActions={
        <ExportCsvButton
          fetchCsv={fetchCsv}
          fileName="unsubscription-during-evolution.csv"
        />
      }
    />
  );
}

export default TrialUnsubscriptionsCard;
